import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Wallet, TrendingUp, AlertTriangle, Clock, Receipt, BellRing } from 'lucide-react';
import api from '../../services/api';
import { useI18n } from '../../i18n/I18nProvider';
import { errorText } from '../../lib/errors';
import { PageHeader, StatCard, DataTable, StatusBadge, Avatar, Badge, EmptyState, Button } from '../../ui';

const PAYMENT_TONE = { completed: 'success', pending: 'warning', refunded: 'neutral' };
const BILLING_RANK = { overdue: 0, due_soon: 1, ok: 2, none: 3 };

export default function BillingPage() {
  const { t, fmt } = useI18n();
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ov, pay] = await Promise.all([api.get('/admin/platform/overview'), api.get('/admin/platform/payments')]);
      setOverview(ov.data);
      setPayments(pay.data.items || []);
    } catch (err) {
      toast.error(errorText(err, t('common.error')));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const companies = useMemo(
    () => [...(overview?.companies ?? [])].sort((a, b) => BILLING_RANK[a.billing_state] - BILLING_RANK[b.billing_state]),
    [overview]
  );
  const mrr = overview?.totals.mrr_by_currency?.UAH ?? 0;

  const companyCell = (name, code) => (
    <div className="flex items-center gap-3">
      <Avatar name={name} size="sm" className="rounded-md" />
      <div>
        <p className="font-medium text-zinc-900 dark:text-zinc-100">{name}</p>
        <p className="font-mono text-[11px] text-zinc-400">{code}</p>
      </div>
    </div>
  );

  const planColumns = [
    { key: 'name', header: t('companies.company'), sortable: true, render: (c) => companyCell(c.name, c.company_code) },
    { key: 'plan', header: t('companies.plan'), sortable: true, render: (c) => t(`plans.${c.plan}`) },
    {
      key: 'plan_price',
      header: t('companies.price'),
      sortable: true,
      align: 'right',
      hideOnMobile: true,
      render: (c) => (
        <div className="text-right">
          <p className="tabular text-zinc-900 dark:text-zinc-100">{fmt.money(c.plan_price, c.currency)}</p>
          <p className="text-xs text-zinc-500">{t(`cycles.${c.billing_cycle}`)}</p>
        </div>
      ),
    },
    { key: 'monthly_price', header: t('billing.monthly'), sortable: true, align: 'right', render: (c) => <span className="tabular">{fmt.money(c.monthly_price, c.currency)}</span> },
    { key: 'billing_next_date', header: t('billing.nextDate'), sortable: true, sortValue: (c) => c.billing_next_date || '9999', render: (c) => <span className="tabular">{fmt.date(c.billing_next_date)}</span> },
    { key: 'billing_state', header: t('common.status'), sortable: true, sortValue: (c) => BILLING_RANK[c.billing_state], render: (c) => <StatusBadge kind="billing" value={c.billing_state} /> },
  ];

  const paymentColumns = [
    { key: 'payment_date', header: t('billing.paymentDate'), sortable: true, render: (p) => <span className="tabular">{fmt.date(p.payment_date)}</span> },
    { key: 'tenant_name', header: t('companies.company'), sortable: true, render: (p) => companyCell(p.tenant_name, p.company_code) },
    { key: 'amount', header: t('billing.amount'), sortable: true, align: 'right', render: (p) => <span className="tabular font-medium text-zinc-900 dark:text-zinc-100">{fmt.money(p.amount, p.currency)}</span> },
    {
      key: 'period',
      header: t('billing.period'),
      hideOnMobile: true,
      render: (p) => (p.period_start || p.period_end ? <span className="tabular text-zinc-500">{fmt.date(p.period_start)} – {fmt.date(p.period_end)}</span> : '—'),
    },
    { key: 'payment_method', header: t('billing.method'), hideOnMobile: true, render: (p) => t(`billing.methods.${p.payment_method}`) },
    { key: 'status', header: t('billing.paymentStatus'), render: (p) => <Badge tone={PAYMENT_TONE[p.status] ?? 'neutral'}>{t(`billing.paymentStatuses.${p.status}`)}</Badge> },
  ];

  return (
    <>
      <PageHeader
        title={t('billing.title')}
        description={t('billing.subtitle')}
        actions={<Link to="/owner/billing/reminders"><Button variant="secondary" icon={BellRing}>{t('reminders.title')}</Button></Link>}
      />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard label={t('billing.mrr')} value={overview ? fmt.money(mrr) : '—'} icon={Wallet} tone="brand" />
        <StatCard label={t('billing.arr')} value={overview ? fmt.money(mrr * 12) : '—'} icon={TrendingUp} />
        <StatCard label={t('billing.overdue')} value={overview ? fmt.number(overview.totals.overdue) : '—'} icon={AlertTriangle} tone={overview?.totals.overdue ? 'danger' : 'neutral'} />
        <StatCard label={t('billing.dueSoon')} value={overview ? fmt.number(overview.totals.due_soon) : '—'} icon={Clock} tone={overview?.totals.due_soon ? 'warning' : 'neutral'} />
      </div>

      <h2 className="mb-3 mt-8 text-sm font-semibold text-zinc-900 dark:text-zinc-100">{t('billing.plansTitle')}</h2>
      <DataTable
        columns={planColumns}
        rows={companies}
        loading={loading}
        onRowClick={(c) => navigate(`/owner/companies/${c.id}?tab=billing`)}
      />

      <h2 className="mb-3 mt-8 text-sm font-semibold text-zinc-900 dark:text-zinc-100">{t('billing.paymentsTitle')}</h2>
      <DataTable
        columns={paymentColumns}
        rows={payments}
        loading={loading}
        onRowClick={(p) => navigate(`/owner/companies/${p.tenant_id}?tab=billing`)}
        empty={<EmptyState icon={Receipt} title={t('billing.noPayments')} />}
      />
    </>
  );
}
